/* eslint-disable react-hooks/exhaustive-deps */
import _ from "lodash";
import { stringify } from "qs";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { RouteComponentProps, withRouter, useHistory } from "react-router-dom";
import { z } from "zod";

import { PageTitle } from "../../components/Common/PageTitle";
import { FacilityList } from "../../components/FacilityListComponents/FacilityList";
import { ModalFilterCondition } from "../../components/FacilityListComponents/ModalFilterCondition";
import { FACILITY_SORT_BY, ORDER, FACILITY_SORT } from "../../config";
import { useUpdateImpersonatedOrganizationContext } from "../../hooks/organizationImpersonation";
import {
  useCategories,
  useSearchQuery,
  useMe,
  useLocationMunis,
} from "../../hooks/queries";
import { canAccessOrganization } from "../../hooks/validateUserOrganization";
import {
  FacilityFilterConditionData,
  createDefaultConditionFormData,
} from "../../models/FacilityFilterConditionData";
import { LocationData } from "../../models/LocationData";
import {
  MunicipalityGroupData,
  convertToGroupData,
} from "../../models/MunicipalityData";
import LocalStorageSvc from "../../services/localStorageSvc";
import {
  convertFormDataToQueryParams,
  convertSearchQueryToConditionFormData,
} from "../../utils/conditionFormDataHelpers";
import useLabels from "../FacilityDetailsPage/hooks/useLabels";
import "./styles.scss";
import useLocations from "../FacilityDetailsPage/hooks/useLocations";

const defaultFilterFormData = {
  searchName: "",
  sort: FACILITY_SORT.STATS_PROFILE_VIEWS,
  order: ORDER.DESC,
  sortBy: FACILITY_SORT_BY.PROFILE_VIEWS_DESC,
  numberPerPage: 20,
  pageIndex: 1,
};

interface IFilterFormData {
  searchName: string;
  sort: typeof FACILITY_SORT;
  order: typeof ORDER;
  sortBy: typeof FACILITY_SORT_BY;
  numberPerPage: number;
  pageIndex: number;
}

const defaultConditionFormData = createDefaultConditionFormData();

type IFacilityListPageProps = RouteComponentProps<any>;

const FacilityListPage: React.FunctionComponent<IFacilityListPageProps> = ({
  match,
}) => {
  const { t: _t } = useTranslation();
  const t = (key: string) => _t(`facilityListPage.${key}`);

  const history = useHistory();
  const configData =
    LocalStorageSvc.getItem(
      "FacilityListPage_Config",
      z.object({
        filterFormData: z.any().optional(),
        searchQuery: z.any().optional(),
      })
    ) || {};
  const searchQuery = useSearchQuery();

  const [locationsData, setLocationsData] = useState<LocationData[]>([]);
  const [totalLocations, setTotalLocations] = useState(0);
  const [shownModalFilterCondition, setShownModalFilterCondition] =
    useState<boolean>(false); // false
  const [organizationId, setOrganizationId] = useState();

  const [conditionFormData, setConditionFormData] =
    useState<FacilityFilterConditionData>(defaultConditionFormData);
  const [filterFormData, setFilterFormData] = useState<IFilterFormData>(
    configData?.filterFormData || defaultFilterFormData
  );

  const [municipalitiesData, setMunicipalitiesData] = useState<
    MunicipalityGroupData[]
  >([]);
  const { data: me } = useMe();

  const { data: locations } = useLocations(
    organizationId,
    searchQuery,
    filterFormData,
    conditionFormData
  );

  const { data: labels } = useLabels(
    _.pickBy({
      organization_id: organizationId,
    })
  );
  const { data: categories } = useCategories();
  const { data: municipalities } = useLocationMunis(
    me?.id,
    organizationId || String(match.params.organizationId ?? "")
  );

  useEffect(() => {
    if (municipalities) {
      const municipalitiesArray = convertToGroupData(municipalities);
      setMunicipalitiesData(municipalitiesArray);
    }
  }, [municipalities]);

  useEffect(() => {
    if (locations) {
      setLocationsData(locations.list);
      setTotalLocations(locations.total);
    }
  }, [locations]);

  const updateImpersonatedOrganizationContext =
    useUpdateImpersonatedOrganizationContext();

  useEffect(() => {
    if (!me) {
      return;
    }

    const userOrgId = me.organization?.id;
    const matchOrganizationId = match.params.organizationId;
    if (matchOrganizationId && canAccessOrganization(matchOrganizationId, me)) {
      setOrganizationId(matchOrganizationId);
      updateImpersonatedOrganizationContext(matchOrganizationId);
    } else if (userOrgId) {
      history.push(`/locations/organization/${userOrgId}`);
    } else if (!userOrgId && matchOrganizationId) {
      history.push(`/locations/`);
    }

    return () => {
      updateImpersonatedOrganizationContext(null);
    };
  }, [match.params.organizationId, me]);

  useEffect(() => {
    const formData = convertSearchQueryToConditionFormData(
      searchQuery,
      municipalitiesData,
      categories,
      labels
    );
    setConditionFormData(formData);
  }, [municipalitiesData, labels, categories, searchQuery]);

  // on Apply Filter Condition
  const onApplyFilterCondition = (
    conditionFormDataApply: FacilityFilterConditionData
  ) => {
    const queryParams = convertFormDataToQueryParams(conditionFormDataApply);
    LocalStorageSvc.setItem("FacilityListPage_Config", {
      filterFormData,
      searchQuery: queryParams,
    });
    history.replace({
      search: stringify(queryParams, { arrayFormat: "repeat" }),
    });

    setShownModalFilterCondition(false);
  };

  // on Click Clear Filter Condition
  const onClickClearFilterCondition = () => {
    LocalStorageSvc.setItem("FacilityListPage_Config", {});
    history.replace({
      search: "",
    });
  };

  return (
    <>
      <div className="right-content facility-list">
        <div className="top-title-bar flex-grid">
          <PageTitle text={t("facilities")} />
          {/* TODO:
              <div className="rights">
                <a href="#javascript" className="btn btn-border">
                  <i className="icons icon-pdf"></i>
                  PDF
                </a>
                <a href="#javascript" className="btn btn-border">
                  <i className="icons icon-csv"></i>
                  CSV
                </a>
              </div>
              */}
        </div>
        {!!locationsData && (
          <FacilityList
            dataList={locationsData}
            totalCount={totalLocations}
            conditionFormData={conditionFormData}
            filterFormData={filterFormData}
            organizationId={organizationId || ""}
            onClickFilter={() => {
              setShownModalFilterCondition(true);
            }}
            onClickClearFilterCondition={() => {
              onClickClearFilterCondition();
            }}
            onChangeFilterFormData={(filterFormData: any) => {
              setFilterFormData(filterFormData);
            }}
          />
        )}
      </div>
      {shownModalFilterCondition && (
        <ModalFilterCondition
          organizationId={organizationId || ""}
          userId={me?.id}
          hideHealthIndex={true}
          hideMunicipalities={true}
          hideCategories={true}
          hideLabels={true}
          onClose={() => {
            setShownModalFilterCondition(false);
          }}
          conditionFormData={conditionFormData}
          onApply={(conditionFormDataApply: FacilityFilterConditionData) => {
            onApplyFilterCondition(conditionFormDataApply);
          }}
        />
      )}
    </>
  );
};

export default withRouter(FacilityListPage);
